/**
 * Sprint BG-DataSettings 14 — 写真 contain 整形 helper。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/photoPreparation.ts` を L2 で踏襲。
 *
 * 用途:
 *   - card cell 内の photo 領域に対し `object-fit: contain` 相当の整形を canvas で先に焼き付け、
 *     後続の `<img>` または jsPDF `addImage` で品質劣化なく描画する。
 *   - html2canvas は `object-fit` を honor しないため canvas で事前整形が必須。
 *
 * 旧との差分:
 *   - jsdom テスト容易化のため `loadImage` を pure helper として export
 *     (canvas/Image を実行時に直接触らない unit test を可能にする `framePhotoContain` も export)。
 *   - cache キーは旧と同等 (URL + 寸法 + 背景 + scale + quality)。
 */

const CSS_PX_PER_MM = 96 / 25.4;

export interface PrepareOptions {
  widthMm: number;
  heightMm: number;
  background?: string;
  pixelScale?: number;
  jpegQuality?: number;
}

type ResolvedOptions = Required<PrepareOptions>;

const cache = new Map<string, string>();

export function buildPhotoCacheKey(url: string, opts: ResolvedOptions): string {
  return `${url}|${opts.widthMm}x${opts.heightMm}|${opts.background}|${opts.pixelScale}|${opts.jpegQuality}`;
}

/**
 * 与えた image / target 寸法に対し contain (黒帯あり) の描画位置を返す pure helper。
 * test 容易、canvas/Image 非依存。
 */
export function framePhotoContain(
  imgNaturalW: number,
  imgNaturalH: number,
  targetW: number,
  targetH: number,
): { x: number; y: number; w: number; h: number } {
  if (imgNaturalW <= 0 || imgNaturalH <= 0 || targetW <= 0 || targetH <= 0) {
    return { x: 0, y: 0, w: targetW, h: targetH };
  }
  const imgRatio = imgNaturalW / imgNaturalH;
  const targetRatio = targetW / targetH;
  if (imgRatio > targetRatio) {
    const w = targetW;
    const h = targetW / imgRatio;
    return { x: 0, y: (targetH - h) / 2, w, h };
  }
  const h = targetH;
  const w = targetH * imgRatio;
  return { x: (targetW - w) / 2, y: 0, w, h };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (typeof Image === 'undefined') {
      reject(new Error('Image 非対応の環境です'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.referrerPolicy = 'no-referrer';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
}

/**
 * 写真 URL を card 内 photo 領域 (widthMm x heightMm) に contain 整形した
 * JPEG data URL に変換。失敗時 (CORS taint 等) は null。
 */
export async function prepareContainPhoto(
  url: string | null,
  options: PrepareOptions,
): Promise<string | null> {
  if (!url) return null;
  const opts: ResolvedOptions = {
    background: '#FFFFFF',
    pixelScale: 4,
    jpegQuality: 0.92,
    ...options,
  };
  const key = buildPhotoCacheKey(url, opts);
  const cached = cache.get(key);
  if (cached) return cached;

  let img: HTMLImageElement;
  try {
    img = await loadImage(url);
  } catch {
    return null;
  }

  if (typeof document === 'undefined') return null;
  const targetW = Math.round(opts.widthMm * CSS_PX_PER_MM * opts.pixelScale);
  const targetH = Math.round(opts.heightMm * CSS_PX_PER_MM * opts.pixelScale);
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = opts.background;
  ctx.fillRect(0, 0, targetW, targetH);

  const frame = framePhotoContain(img.naturalWidth, img.naturalHeight, targetW, targetH);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, frame.x, frame.y, frame.w, frame.h);

  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL('image/jpeg', opts.jpegQuality);
  } catch (e) {
    // CORS taint 等で toDataURL が失敗した場合は null (caller が `(写真なし)` 表示)
    console.warn('[card-print] canvas toDataURL failed (CORS taint?)', e);
    return null;
  }
  cache.set(key, dataUrl);
  return dataUrl;
}

export function clearPhotoCache(): void {
  cache.clear();
}
